import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { Collapse, IconButton } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";

import { RoutesConstants } from "@/main/routes/routesConstants";
import {
	getGuidScreenByRoute,
	getTranslateByKey,
	isNullUndefined,
	isShowControlByElementId,
} from "@/main/services/utils";
import { userIsBodyshop, userIsInsurer } from "@/main/services/utilsUser";

import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import ClaimDetails from "@/main/components/shared/claimDetails/claimDetails";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import ProcurementStatusComponent from "@/main/components/shared/status/procurementStatusComponent";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import { GetItemsBySupplierRequest } from "@/main/models/request";
import {
	GetImagePartResponse,
	ManageStatusItemsResponse,
	ProcurementSupplierItem,
	QuoteDataResponse,
} from "@/main/models/response";
import { Controls } from "@/main/services/constants/constantsControl";
import { OrderStepsEnum } from "@/main/services/enum/OrderStepsEnum";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";

import AwaitingApprovalCollapse from "@/main/screens/order/viewOrder/components/awaitingApprovalCollapse/awaitingApprovalCollapse";
import { IGridData } from "../../orderScreen";
import CollapseQuoteStep3 from "../collapseQuoteStep3/collapseQuoteStep3";
import InfoOrder from "../infoOrder/infoOrder";
import * as S from "./styles";
import * as T from "./types";
import { useAutomaticActionReducer } from "@/main/store/automaticAction";
import { AutomaticActionEnum } from "@/main/services/enum/AutomaticActionEnum";

interface IContentCardProps {
	each: QuoteDataResponse;
	gridData: IGridData;
	setGridData?: (data: IGridData) => void;
	handleOpenModalTrancking: ({ guidQuoteProcurement }: GetItemsBySupplierRequest) => void;
	handleUpdateQuoteCountStatus: () => void;
	handleUpdateQuoteGridStatus: (guid: string, newStatus: number, newStatusSupplierName: string) => void;
	refreshRowByGuidProcurement: T.refreshRowByGuidProcurementType;
	refreshGrid?: () => void;
}

const ContentCard = ({
	each,
	handleOpenModalTrancking,
	handleUpdateQuoteCountStatus,
	handleUpdateQuoteGridStatus,
	refreshRowByGuidProcurement,
	setGridData,
	gridData,
	refreshGrid,
}: IContentCardProps) => {
	const navigate = useNavigate();
	const STATUS_QUOTE_COLLAPSE = [
		ProcurementStatusEnum.WaitingRelease,
		ProcurementStatusEnum.Manual,
		ProcurementStatusEnum.Open,
	];
	const isManualOrAwaitingRelease = STATUS_QUOTE_COLLAPSE.includes(Number(each.idStatus));
	const isQuote =
		Number(each.idStatus) === ProcurementStatusEnum.Open ||
		Number(each.idStatus) === ProcurementStatusEnum.Manual ||
		Number(each.idStatus) === ProcurementStatusEnum.WaitingRelease ||
		(Number(each.idStatus) === ProcurementStatusEnum.Cancelled && isNullUndefined(each.orderNumber));
	const isAwaitingApproval = Number(each.idStatus) === ProcurementStatusEnum.AwaitingApproval;
	const isCollapsed = !isQuote || (isQuote && isManualOrAwaitingRelease && !userIsBodyshop());

	const [expanded, setExpanded] = useState(false);

	const { automaticAction } = useAutomaticActionReducer();

	// Handle Functions
	function handleClickRow(e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) {
		e.stopPropagation();
		if (openModalClaimDetails) return;
		if (Number(each.idStatus) === ProcurementStatusEnum.PreQuote) {
			return navigate(`${RoutesConstants.orderPreQuote}?guidQuote=${each.guidQuoteProcurement}`);
		}
		if (isQuote && !isCollapsed) {
			const isQuoteCanceled = Number(each.idStatus) === ProcurementStatusEnum.Cancelled;
			const routeNavigate = isQuoteCanceled
				? `${RoutesConstants.orderCreateOrder}?guidQuote=${each.guidQuote}&step=${OrderStepsEnum.Summary}`
				: `${RoutesConstants.orderCreateOrder}?guidQuote=${each.guidQuote}&step=${OrderStepsEnum.Parts}`;

			return navigate(routeNavigate);
		}
		setExpanded(!expanded);
	}
	function renderText(text: string | number | undefined, hasTooltip: boolean = false) {
		if (!text) return "-";
		if (hasTooltip)
			return (
				<TooltipComponent title={text as string} placement="top">
					<S.TooltipText>{text as string}</S.TooltipText>
				</TooltipComponent>
			);

		return String(text);
	}

	// CONSTANTS LAYOUT
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const showColumnBodyshop = !userIsBodyshop();
	const showColumnInsurer = !userIsInsurer();
	const hideColumn = !showColumnInsurer || !showColumnBodyshop;

	const numberOfColumnsHidden = useMemo(() => {
		let numberhide = 0;
		if (hideColumn) numberhide += 1;
		return numberhide;
	}, [hideColumn]);

	const [openModalClaimDetails, setOpenModalClaimDetails] = useState<boolean>(false);
	const [resultDataManageStatus, setResultDataManageStatus] = useState<ManageStatusItemsResponse | null>(null);

	function getContentTypeCollapse(): T.IContentTypeCollapse {
		if (isNullUndefined(each) || (isQuote && (!isManualOrAwaitingRelease || userIsBodyshop()))) return "none";
		if (isAwaitingApproval) return "awaitingApproval";
		if (isManualOrAwaitingRelease) return "quote";
		if (each.guidQuoteProcurement) return "order";

		return "none";
	}

	// #region Rule for updating the total number of items in the order when performing the action of Canceling or returning the item
	useEffect(() => {
		if (
			!isNullUndefined(resultDataManageStatus) &&
			!isNullUndefined(resultDataManageStatus.orderGuid) &&
			!isNullUndefined(resultDataManageStatus.orderTotalPriceFormatted) &&
			!isNullUndefined(gridData) &&
			!isNullUndefined(gridData.data) &&
			gridData.data.length > 0 &&
			setGridData
		) {
			const procurement = gridData.data.find(
				(quoteProcurement: QuoteDataResponse) =>
					String(quoteProcurement.guidQuoteProcurement).toLowerCase() ===
					String(resultDataManageStatus.orderGuid).toLowerCase(),
			);
			if (
				!isNullUndefined(procurement) &&
				procurement.formattedTotalPricePart !== resultDataManageStatus.orderTotalPriceFormatted
			) {
				setGridData({
					...gridData,
					data: gridData.data.map((quoteProcurement: QuoteDataResponse) => {
						if (
							String(quoteProcurement.guidQuoteProcurement).toLowerCase() ===
							String(resultDataManageStatus.orderGuid).toLowerCase()
						) {
							const quoteProcurementUpdated = quoteProcurement;
							quoteProcurementUpdated.formattedTotalPricePart = resultDataManageStatus.orderTotalPriceFormatted || "";
							return quoteProcurementUpdated;
						}

						return quoteProcurement;
					}),
				});
			}
		}
	}, [resultDataManageStatus]);

	useEffect(() => {
		if (isNullUndefined(each) || (isQuote && !isManualOrAwaitingRelease)) {
			setExpanded(false);
		}
	}, [each.idStatus]);

	useEffect(() => {
		const findAction = automaticAction.data?.find(
			(x) => x.idAction == AutomaticActionEnum.GenerateOrderBySaveBuyerPartAsnwer && x.id == each.guidQuote,
		);

		if (isNullUndefined(findAction)) return;

		setExpanded(true);
	}, [automaticAction.data, each.guidQuote]);
	// #endregion

	// Constants Layout

	const contentTypeCollapse = getContentTypeCollapse();

	return (
		<>
			<S.ContentContainer>
				<S.Content
					id={`id-searchOrder-row-order-${each.guidQuoteProcurement || each.guidQuote}`}
					$numberOfColumnsHidden={numberOfColumnsHidden}
					onClick={handleClickRow}
				>
					<ColumnLabel label={getTranslateByKey("information.order")} content={renderText(each.orderNumber, true)} />
					<ColumnLabel
						label={
							<S.ContainerDetail>
								{getTranslateByKey("information.claimNumber")}
								{each.integration && each.showButtonIconSeeClaimDetails && (
									<ClaimDetails
										guidScreen={getGuidScreenByRoute(RoutesConstants.orderSearchOrder)}
										showPrices={showPrices}
										openModalClaimDetails={openModalClaimDetails}
										guidQuote={each.guidQuote}
										setOpenModalClaimDetails={setOpenModalClaimDetails}
									/>
								)}
							</S.ContainerDetail>
						}
						content={<S.ContainerDetail>{renderText(each.claimNumber, true)}</S.ContainerDetail>}
					/>
					<ColumnLabel label={getTranslateByKey("information.quote")} content={renderText(each.code, true)} />
					<ColumnLabel label={getTranslateByKey("information.vehicle")} content={renderText(each.vehicle, true)} />
					{showColumnInsurer && (
						<ColumnLabel
							label={getTranslateByKey("information.insurer")}
							content={renderText(each.insurerCompanyName, true)}
						/>
					)}
					{showColumnBodyshop && (
						<ColumnLabel
							label={getTranslateByKey("information.bodyshop")}
							content={renderText(each.bodyShopCompanyName, true)}
						/>
					)}
					<ColumnLabel
						label={getTranslateByKey("information.createDate")}
						content={<span translate="no">{renderText(each.startDateFormatted, true)}</span>}
					/>
					<ColumnLabel
						label=""
						content={<ProcurementStatusComponent status={{ id: Number(each.idStatus), name: each.nameStatus }} />}
						propsLabel={{ style: { textAlign: "center" } }}
					/>
					<S.AreaIcon>
						<IconButton
							onClick={handleClickRow}
							id={`id-searchOrder-button-${
								(isQuote && (!isManualOrAwaitingRelease || userIsBodyshop())) ||
								Number(each.idStatus) === ProcurementStatusEnum.PreQuote
									? "icon"
									: "colapse"
							}`}
						>
							{(isQuote && (!isManualOrAwaitingRelease || userIsBodyshop())) ||
							Number(each.idStatus) === ProcurementStatusEnum.PreQuote ? (
								<SearchIcon />
							) : expanded === false ? (
								<ExpandMore aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							) : (
								<ExpandLess aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							)}
						</IconButton>
					</S.AreaIcon>
				</S.Content>
			</S.ContentContainer>
			{isCollapsed && (
				<Collapse in={expanded} timeout="auto" mountOnEnter>
					<S.ContentCollapse>
						{contentTypeCollapse == "order" && (
							<InfoOrder
								guidCompany={each.guidCompany}
								bodyshopCompanyGuid={each.bodyshopCompanyGuid}
								handleOpenModalTrancking={handleOpenModalTrancking}
								resultDataManageStatus={resultDataManageStatus}
								idStatusQuote={Number(each.idStatus)}
								guidQuote={each.guidQuote}
								orderNumber={each.orderNumber}
								claimNumber={String(each.claimNumber)}
								bodyShopCompanyName={each.bodyShopCompanyName}
								insurerCompanyName={each.insurerCompanyName}
								rowGrid={each}
								handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
								handleUpdateQuoteGridStatus={handleUpdateQuoteGridStatus}
								refreshRowByGuidProcurement={refreshRowByGuidProcurement}
								setResultDataManageStatus={setResultDataManageStatus}
								refreshGrid={refreshGrid}
								guidClaim={each.guidClaim}
							/>
						)}
						{contentTypeCollapse === "quote" && (
							<CollapseQuoteStep3
								guidQuote={each.guidQuote}
								handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
								handleUpdateQuoteGridStatus={handleUpdateQuoteGridStatus}
								refreshGrid={refreshGrid}
								guidClaim={each.guidClaim}
								vehicleName={each.vehicle}
								orderNumber={each.orderNumber}
							/>
						)}
						{contentTypeCollapse === "awaitingApproval" && (
							<AwaitingApprovalCollapse
								data={each}
								refreshGrid={refreshGrid}
								handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
							/>
						)}
					</S.ContentCollapse>
				</Collapse>
			)}
		</>
	);
};

export default function CardGridOrder({
	title,
	gridData,
	formFilters,
	infiniteScrollConfig,
	handleOpenModalTrancking,
	handleUpdateQuoteCountStatus,
	handleUpdateQuoteGridStatus,
	refreshRowByGuidProcurement,
	setGridData,
	refreshGrid,
}: T.ICardGridProps) {
	function renderGrid() {
		return (
			<S.ContainerCard>
				{gridData.data.map((each) => (
					<ContentCard
						key={each.guidQuoteProcurement}
						each={each}
						handleOpenModalTrancking={handleOpenModalTrancking}
						handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
						handleUpdateQuoteGridStatus={handleUpdateQuoteGridStatus}
						refreshRowByGuidProcurement={refreshRowByGuidProcurement}
						setGridData={setGridData}
						gridData={gridData}
						refreshGrid={refreshGrid}
					/>
				))}
			</S.ContainerCard>
		);
	}
	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;
	return (
		<S.Container id="id-general-cardGridOrder">
			{title && <S.Title>{title}</S.Title>}

			{infiniteScrollConfig ? (
				<div style={{ minHeight: "95vh" }}>
					<InfiniteScroll
						style={{ overflowX: "hidden" }}
						dataLength={infiniteScrollConfig.dataLength}
						next={infiniteScrollConfig.fetchData}
						hasMore={infiniteScrollConfig.hasMore}
						loader={infiniteScrollConfig.isLoading ? <LoadingComponent /> : <></>}
						endMessage={
							<S.EndMessage className="d-flex d-flex justify-content-center">
								{getTranslateByKey("information.allResultsWereDisplayed")}
							</S.EndMessage>
						}
					>
						{renderGrid()}
					</InfiniteScroll>
				</div>
			) : (
				renderGrid()
			)}
		</S.Container>
	);
}

export interface IHandleSetInfo {
	guidSupplierItem: string;
	open: boolean;
	idReasonType: number;
	guidReason?: string;
	reason?: string;
	reasonDescription?: string;
	guidProcurement: string;
	guidProcurementSupplier: string;
	guidProcurementSupplierItem?: string;
	idProcurementStatusCompany: number;
	procurementSupplierItems: ProcurementSupplierItem[];
	imagesReturnProcess?: GetImagePartResponse[];
	isBuyer?: boolean;
}
